import { useMemo, useState, useEffect } from 'react';
import { Card, CardBody, Chip, Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, useDisclosure } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  type MapperItem,
  RouteState,
  useDeleteMapperMutation,
  useGetMappersListQuery,
  useTypedSelector,
} from '@circuitry-ai/doc-data';
import {
  AgGridTable,
  CardListViewMode,
  CtyButton,
  ListActionButtonsCell,
  SearchWithViewToggle,
  checkType,
  CtyShowToast,
  ErrorBoundary,
  PageHeader,
  getActionsColumnDef,
  SimpleDeleteConfirmationModal,
} from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const MAPPER_CONFIGURATION_TAB = 'configuration';
const MAPPER_EXECUTE_TAB = 'execute';

function getMapperRows(response: unknown): MapperItem[] {
  if (Array.isArray(response)) return response as MapperItem[];
  if (response && typeof response === 'object') {
    const r = response as Record<string, unknown>;
    if (Array.isArray(r.data)) return r.data as MapperItem[];
    if (Array.isArray(r.mappers)) return r.mappers as MapperItem[];
  }
  return [];
}

function getMapperStatus(mapper: MapperItem): string {
  return String(mapper.mapper_status ?? mapper.mapper_definition?.status ?? '-');
}

function SchemaMapperCardGrid({
  mappers,
  onEditMapper,
  onOpenMapper,
  onDeleteMapper,
}: {
  readonly mappers: MapperItem[];
  readonly onEditMapper: (mapper: MapperItem) => void;
  readonly onOpenMapper: (mapper: MapperItem) => void;
  readonly onDeleteMapper: (mapper: MapperItem) => void;
}) {
  const { t } = useTranslation();

  if (mappers.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center h-full border-2 border-default-300 rounded-lg m-1 bg-default-100">
        <div className="text-2xl text-primary">{t('No Schema Mappers found')}</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {mappers.map((mapper) => (
        <Card key={mapper.aiworker_id} shadow="sm" className="border border-default-200">
          <CardBody className="gap-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 text-base font-semibold text-foreground truncate">
                {mapper.name ?? '-'}
              </div>
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    variant="light"
                    className="h-7 min-w-0 p-1"
                    isIconOnly
                    aria-label={t('Mapper actions')}
                  >
                    <Icon icon="entypo:dots-three-vertical" style={{ color: '#D9D9D9' }} />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu aria-label={t('Mapper actions menu')}>
                  <DropdownItem
                    key="delete"
                    className="text-danger"
                    title={t('Delete')}
                    onPress={() => onDeleteMapper(mapper)}
                  >
                    {t('Delete')}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
            <div className="flex items-center justify-between">
              <Chip size="sm" variant="flat" color={getMapperStatus(mapper).toUpperCase() === 'APPROVED' ? 'success' : 'warning'}>
                {t(getMapperStatus(mapper))}
              </Chip>
            </div>
            <div className="flex items-center gap-2">
              <CtyButton
                className="w-full border-1"
                variant="bordered"
                color="default"
                onPress={() => onEditMapper(mapper)}
              >
                {t('Edit')}
              </CtyButton>
              <CtyButton
                className="w-full"
                variant="solid"
                color="primary"
                onPress={() => onOpenMapper(mapper)}
              >
                {t('Execute')}
              </CtyButton>
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

const SchemaMapperCardGridWithErrorBoundary = ErrorBoundary(SchemaMapperCardGrid);

export default function SchemaMapperPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useTypedSelector((state) => state.auth.token);
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const [skip, setSkip] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);
  const [mapperToDelete, setMapperToDelete] = useState<MapperItem | null>(null);
  const [deleteMapper, { isLoading: isDeletingMapper }] = useDeleteMapperMutation();

  const {
    data: mapperResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetMappersListQuery(undefined, { skip, refetchOnMountOrArgChange: true });

  useEffect(() => {
    if (token && skip) {
      setSkip(false);
    }
  }, [skip, token]);

  const mapperRows = useMemo(() => getMapperRows(mapperResponse), [mapperResponse]);

  const filteredMappers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return mapperRows;
    return mapperRows.filter((mapper) => String(mapper.name ?? '').toLowerCase().includes(q));
  }, [mapperRows, searchQuery]);

  const listRows = useMemo(
    () =>
      filteredMappers.map((mapper) => ({
        aiworker_id: mapper.aiworker_id,
        name: mapper.name ?? '-',
        mapper_status: getMapperStatus(mapper),
        mapper_raw: mapper,
      })),
    [filteredMappers]
  );

  const handleEditMapper = (mapper: MapperItem) => {
    navigate(`/settings/schema-mapper/${RouteState.UPDATE}/${mapper.aiworker_id}?tab=${MAPPER_CONFIGURATION_TAB}`, {
      state: { mapper },
    });
  };

  const handleOpenMapper = (mapper: MapperItem) => {
    navigate(`/settings/schema-mapper/${RouteState.UPDATE}/${mapper.aiworker_id}?tab=${MAPPER_EXECUTE_TAB}`, {
      state: { mapper },
    });
  };

  const handleCreateMapper = () => {
    navigate(`/settings/schema-mapper/${RouteState.CREATE}?tab=${MAPPER_CONFIGURATION_TAB}`);
  };

  const handleOpenDeleteModal = (mapper: MapperItem) => {
    setMapperToDelete(mapper);
    onOpen();
  };

  const handleConfirmDelete = async () => {
    if (!mapperToDelete?.aiworker_id) {
      CtyShowToast(checkType.error, t('Mapper ID not found'));
      return;
    }

    try {
      await deleteMapper(mapperToDelete.aiworker_id).unwrap();
      CtyShowToast(
        checkType.success,
        t('Mapper deleted successfully')
      );
      onClose();
      setMapperToDelete(null);
      refetch();
    } catch (error) {
      CtyShowToast(
        checkType.error,
        (error as Error)?.message ?? t('Delete operation failed')
      );
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: t('Name'),
        field: 'name',
        sortable: true,
        flex: 1.5,
        minWidth: 260,
      },
      {
        headerName: t('Status'),
        field: 'mapper_status',
        sortable: true,
        flex: 1,
        minWidth: 180,
      },
      getActionsColumnDef({
        headerName: t('Actions'),
        minWidth: 240,
        maxWidth: 280,
        cellRenderer: (params: any) => (
          <ListActionButtonsCell
            actions={[
              {
                label: t('Edit'),
                onPress: () => handleEditMapper(params?.data?.mapper_raw),
                variant: 'bordered',
                color: 'default',
              },
              {
                label: t('Execute'),
                onPress: () => handleOpenMapper(params?.data?.mapper_raw),
                variant: 'solid',
                color: 'primary',
              },
              {
                label: t('Delete'),
                onPress: () => handleOpenDeleteModal(params?.data?.mapper_raw),
                variant: 'bordered',
                color: 'danger',
              },
            ]}
          />
        ),
      }),
    ],
    [handleEditMapper, handleOpenMapper, handleOpenDeleteModal, t]
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        heading={t('Schema Mapper')}
        description={t('View and manage all schema mappers in your account.')}
        showCreateButton
        createButtonText={t('Create Mapper')}
        onCreateButtonClick={handleCreateMapper}
      />
      <SearchWithViewToggle
        searchId="schema-mapper-search"
        searchPlaceholder={t('Search schema mapper')}
        onSearch={setSearchQuery}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      <div className={`flex-1 p-3 ${viewMode === CardListViewMode.LIST ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        {viewMode === CardListViewMode.LIST ? (
          <AgGridTable
            tableId="schema-mapper-list-table"
            tableData={listRows}
            tableColumnDefs={columnDefs}
            isLoading={isLoading || isFetching}
          />
        ) : (
          <SchemaMapperCardGridWithErrorBoundary
            isLoading={isLoading || isFetching}
            error={error}
            mappers={filteredMappers}
            onEditMapper={handleEditMapper}
            onOpenMapper={handleOpenMapper}
            onDeleteMapper={handleOpenDeleteModal}
          />
        )}
      </div>
      <SimpleDeleteConfirmationModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        entityName={mapperToDelete?.name ?? ''}
        onConfirm={handleConfirmDelete}
        isConfirmLoading={isDeletingMapper}
        cancelLabel={t('Close')}
        confirmLabel={t('Delete Mapper')}
      />
    </div>
  );
}

export { SchemaMapperPage };
